import {
  FormInput,
  InfoBox,
  TabsNavigation,
  MagellanPopups,
} from "components/molecules";
import TopBar from "components/organisms/top-bar/TopBar";
import { TopbarTabs } from "components/templates";
import { Form, Formik, FormikConfig } from "formik";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { IRegisterUserForm } from "services/userManagement/types";
import { II18nextPrefix } from "types/components/components.interface";
import { object, string } from "yup";
import { IFormikProps, IFormProps } from "../types";
import UserPermissions from "./tabs/UserPermissions";
import { PopupActions } from "reactjs-popup/dist/types";
import { useGetUsers } from "services/userManagement";
import { Button } from "components/atoms";
import { BUTTON_VARIANT } from "types/components/button/button.interface";
import { useFormikContext } from "formik";
import { onErrorShowAllErrorsToast } from "utils/service/errorHandler";
import toast from "react-hot-toast";
import { useGetPermissionsFromUser } from "services/userManagement/managePermissions";
import { useParamId } from "hooks/useParamId";

export enum userTabs {
  permissions,
}

const validationSchema = object().shape({
  userName: string().required(() => {
    return i18next.t("formik-error.required");
  }),
  email: string()
    .required(() => {
      return i18next.t("formik-error.required");
    })
    .email(() => i18next.t("formik-error.invalid-email")),
  // phoneNumber: string().required(i18next.t("formik-error.required")),
  firstName: string().required(i18next.t("formik-error.required")),
  lastName: string().required(i18next.t("formik-error.required")),
});

export const userFormInitialValues: IRegisterUserForm = {
  email: "",
  phoneNumber: "",
  userName: "",
  firstName: "",
  lastName: "",
  isActive: true,
  isTwoFactorEnabled: false,
  permissions: {},
  selectedUserId: "",
};

export interface IUserForm
  extends FormikConfig<IRegisterUserForm>,
    IFormProps,
    II18nextPrefix {}

const UserForm = ({
  i18nextPrefix,
  status,
  topbarContent,
  tittle,
  disableTabs,
  backPath,
  customBackItem,
  initialValues,
  ...props
}: IUserForm) => {
  const { t } = useTranslation();
  const userId = useParamId();

  const { mutateAsync: getPermissions } = useGetPermissionsFromUser();

  const { data } = useGetUsers({ itemCount: 9999 });

  const onImportPermissions = async (
    actions: PopupActions,
    selectedUserId: string,
    setFieldValue: any
  ) => {
    if (!selectedUserId) {
      return toast.error(t("error-code.FORM_ERROR"));
    }

    try {
      const permissions = await getPermissions(selectedUserId);
      setFieldValue(
        "permissions",
        permissions.permissions.reduce((acc, permission) => {
          const domainName = permission.item1.permissionDomainName;

          if (!acc[domainName]) {
            acc[domainName] = {
              domain: domainName,
              permissions: [],
            };
          }

          acc[domainName].permissions.push({
            flag: permission.item1.permissionFlagValue,
            isSelected: permission.item2,
          });

          return acc;
        }, {} as Record<string, { domain: string; permissions: { flag: number; isSelected: boolean }[] }>)
      );

      actions.close();
    } catch (error: any) {
      onErrorShowAllErrorsToast(error);
    }
  };



  // const disableInputs =
  //   !!initialValues.userName ||
  //   !!initialValues.email ||
  //   !!initialValues.phoneNumber ||
  //   !!initialValues.firstName ||
  //   !!initialValues.lastName;

    const disableInputs = false;

  return (
    <Formik
      {...props}
      validationSchema={validationSchema}
      initialStatus={status}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={true}
      initialValues={initialValues}
    >
      {({
        status,
        values,
        errors,
        setFieldValue,
      }: IFormikProps<IRegisterUserForm>) => (
        <Form className="w-full h-full">
          <TopbarTabs>
            <TopBar
              title={tittle}
              customBackItem={customBackItem}
              path={backPath ?? "settings/users"}
              topbarContent={
                <div className="flex w-full gap-4 items-center">
                  <FormInput.Switch
                    name="isActive"
                    label={t("input.label.isActive")}
                    checked={values.isActive}
                    className={`${
                      status?.displayMode
                        ? "pointer-events-none"
                        : "pointer-events-auto"
                    }`}
                  />
                  <div className="w-full flex items-center">
                    <FormInput.Switch
                      className={`${
                        status?.displayMode
                          ? "pointer-events-none"
                          : "pointer-events-auto"
                      }`}
                      name="isTwoFactorEnabled"
                      label={t("input.label.isTwoFactorEnabled")}
                      checked={values.isTwoFactorEnabled}
                    />
                  </div>
                  <div>{topbarContent}</div>
                </div>
              }
            >
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={2}>
                  <FormInput.Text
                    name="userName"
                    label={t("input.label.nickname")}
                    autoFocus
                    disabled={disableInputs}
                  />
                  <FormInput.Text
                    name="firstName"
                    label={t("input.label.firstName")}
                    disabled={disableInputs}
                  />
                  <FormInput.Text
                    name="lastName"
                    label={t("input.label.lastName")}
                    disabled={disableInputs}
                  />
                  <FormInput.Text
                    name="email"
                    label={t("input.label.email")}
                    disabled={disableInputs}
                  />
                  <FormInput.Text
                    name="phoneNumber"
                    label={t("input.label.phoneNumber")}
                    disabled={disableInputs}
                  />
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                disabled={disableTabs}
                tabs={[
                  {
                    value: userTabs.permissions,
                    name: "permissions",
                    error: errors.permissions,
                  },
                ]}
                i18nextPrefix={i18nextPrefix}
              />
              {!status?.displayMode && userId && (
                <div className="buttons w-auto z-50">
                  <MagellanPopups.PopupImportPermissionsForm
                    buttonText={t("button.import")}
                    children={
                      <>
                      <FormInput.Select
                          label={t(`permissions.popup.select`)}
                          name={"selectedUserId"}
                          options={
                            data &&
                            data?.pages
                              .flat()
                              .filter((el) => el.id !== userId)
                              .map((el) => {
                                const { id, fullName } = el;

                                return {
                                  value: id,
                                  label: fullName,
                                };
                              })
                          }
                        />
                      </>
                    }
                    header={t(`permissions.popup.header`)}
                    onAdd={(actions: PopupActions) => {
                      if (!values.selectedUserId) return;
                      onImportPermissions(
                        actions,
                        values.selectedUserId,
                        setFieldValue
                      );
                    }}
                    trigger={
                      <Button 
                        variant={BUTTON_VARIANT.warning}
                      >
                        <Button.Text text={t(`button.import`)} />
                      </Button>
                    }
                  />
                </div>
              )}
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

UserForm.getRouterTabs = (disabledChildren?: string[]) =>
  [{ path: "permissions", element: <UserPermissions /> }].filter(
    (children) => !disabledChildren?.includes(children.path)
  );

export default UserForm;
